import React, { useState } from 'react';
import { getBusinessUnits, type IBusinessUnit } from '../services/businessCaseService';
import { getSharePointExcelFiles, type ISharePointFile } from '../services/sharepointService';
import { BCTContext, type BusinessCase } from './BCTContextDefinition';

export const BCTProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [businessCases] = useState<BusinessCase[]>([]);
    const [selectedBusinessCase, setSelectedBusinessCase] = useState<BusinessCase | null>(null);
    const [loading, setLoading] = useState(false);
    const [businessUnits, setBusinessUnits] = useState<IBusinessUnit[] | null>(null);
    const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
    const [businessUnitsError, setBusinessUnitsError] = useState<string | null>(null);

    const [sharepointFiles, setSharepointFiles] = useState<ISharePointFile[]>([]);
    const [sharepointFilesLoading, setSharepointFilesLoading] = useState(false);
    const [sharepointFilesError, setSharepointFilesError] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const CACHE_KEY = 'businessUnits';
    const CACHE_EXPIRY_HOURS = 24; // Refresh every 24 hours

    const isCacheValid = (cachedData: any) => {
        if (!cachedData) return false;
        const now = Date.now();
        const cacheTime = cachedData.timestamp || 0;
        return (now - cacheTime) < CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    };

    const fetchSharepointFiles = async () => {
        setSharepointFilesLoading(true);
        setSharepointFilesError(null);

        try {
        const files = await getSharePointExcelFiles();
        setSharepointFiles(Array.isArray(files) ? files : []);
        } catch (error: any) {
        setSharepointFilesError(error.message || 'Failed to load SharePoint files');
        console.error('Failed to load SharePoint files:', error);
        } finally {
        setSharepointFilesLoading(false);
        }
    };

  const refreshSharepointFiles = async () => {
    await fetchSharepointFiles();
  };

    const fetchBusinessUnits = async () => {
        setBusinessUnitsLoading(true);
        setBusinessUnitsError(null);

        try {
        // Check localStorage first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const parsed = JSON.parse(cached);
            if (isCacheValid(parsed)) {
            setBusinessUnits(parsed.data);
            setBusinessUnitsLoading(false);
            return;
            }
        }

        // Fetch from API
        const units = await getBusinessUnits();
        setBusinessUnits(units);

        // Cache in localStorage
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: units,
            timestamp: Date.now(),
        }));
        } catch (error: any) {
        setBusinessUnitsError(error.message || 'Failed to load business units');
        // Use cached data if available as fallback
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const parsed = JSON.parse(cached);
            setBusinessUnits(parsed.data);
        }
        } finally {
        setBusinessUnitsLoading(false);
        }
    };

    const refreshBusinessUnits = async () => {
        localStorage.removeItem(CACHE_KEY); // Force refresh
        await fetchBusinessUnits();
    };

     const initializeData = async () => {
        if (isInitialized) return; // Prevent re-initialization
        
        setIsInitialized(true);

        try{
            await Promise.all([
                fetchBusinessUnits(),
                fetchSharepointFiles()
            ]);
        } catch (error) {
            console.error('Error initializing data:', error);
            setIsInitialized(false); // Reset flag on error to allow retry
        }
    };

    return (
        <BCTContext.Provider
            value={{
                businessCases,
                selectedBusinessCase,
                setSelectedBusinessCase,
                loading,
                setLoading,
                businessUnits,
                businessUnitsLoading,
                businessUnitsError,
                refreshBusinessUnits,
                sharepointFiles,
                sharepointFilesLoading,
                sharepointFilesError,
                refreshSharepointFiles,
                initializeData,
                isInitialized,
            }}
        >
            {children}
        </BCTContext.Provider>
    );
};